/**
 * OccupancySensorModalClassic — Monolithic occupancy sensor modal template.
 *
 * Read-only occupancy sensor display panel:
 * - Status: large Occupied/Vacant display with person icon
 * - Info: last detected time placeholder
 *
 * No commands — purely observational.
 */
import { useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './OccupancySensorModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const OCCUPANCY_SENSOR_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Status Section ──
  { id: 'status', label: 'Occupancy Status', icon: 'person_pin', slots: [
    { id: 'status-icon', label: 'Person Icon', editableProps: ['fontSize', 'color'],
      states: [{ id: 'occupied', label: 'Occupied' }, { id: 'vacant', label: 'Vacant' }] },
    { id: 'status-value', label: 'Status Text "OCCUPIED/VACANT"', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'occupied', label: 'Occupied' }, { id: 'vacant', label: 'Vacant' }] },
    { id: 'status-badge', label: 'Status Badge Pill', editableProps: ['background', 'color', 'borderRadius', 'padding', 'fontSize'],
      states: [{ id: 'occupied', label: 'Occupied' }, { id: 'vacant', label: 'Vacant' }] },
  ]},
  // ── Info Section ──
  { id: 'info', label: 'Detection Info', icon: 'info', slots: [
    { id: 'info-label', label: 'Title "DETECTION INFO"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'info-row-icon', label: 'Info Row Icon', editableProps: ['fontSize', 'color'] },
    { id: 'info-row-text', label: 'Info Row Text', editableProps: ['fontSize', 'color'] },
  ]},
]

const DEFAULT_SECTION_ORDER = OCCUPANCY_SENSOR_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  const v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OccupancySensorModalClassic({
  device,
  deviceState,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const rawPresence = getAttr(deviceState, 'presence', 'occupied', 'occupancy')
  const isOccupied = rawPresence === true
    || rawPresence === 'true'
    || rawPresence === 1
    || rawPresence === '1'
    || (isEditorMode && rawPresence === undefined ? true : false)

  const lastDetected = numAttr(deviceState, 'lastDetectedTimestamp', 'lastMotionTime')

  const lastDetectedText = useMemo(() => {
    if (lastDetected === null) return 'No recent detection data'
    const d = new Date(lastDetected * 1000)
    return `Last detected: ${d.toLocaleString()}`
  }, [lastDetected])

  return (
    <div
      className={`occupancy-sensor-modal-classic${isOccupied ? ' is-occupied' : ' is-vacant'}${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="occupancy-sensor-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="occupancy-sensor-modal-classic__header">
        <button className="occupancy-sensor-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="occupancy-sensor-modal-classic__title" data-modal-slot="header-title">{device.name || 'Occupancy Sensor'}</h2>
        <button
          className="occupancy-sensor-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'status':
            return (
              <div key="status" className={`occupancy-sensor-modal-classic__status${hl}`} data-modal-section="status">
                <div className="occupancy-sensor-modal-classic__status-inner">
                  <div className={`occupancy-sensor-modal-classic__status-ring${isOccupied ? ' occupied' : ' vacant'}`}>
                    <span
                      className="material-symbols-rounded occupancy-sensor-modal-classic__status-icon"
                      data-modal-slot="status-icon"
                      data-modal-slot-state={isOccupied ? 'occupied' : 'vacant'}
                    >
                      {isOccupied ? 'person' : 'person_off'}
                    </span>
                  </div>
                  <span
                    className={`occupancy-sensor-modal-classic__status-value${isOccupied ? ' occupied' : ' vacant'}`}
                    data-modal-slot="status-value"
                    data-modal-slot-state={isOccupied ? 'occupied' : 'vacant'}
                  >
                    {isOccupied ? 'OCCUPIED' : 'VACANT'}
                  </span>
                  <span
                    className={`occupancy-sensor-modal-classic__status-badge${isOccupied ? ' occupied' : ' vacant'}`}
                    data-modal-slot="status-badge"
                    data-modal-slot-state={isOccupied ? 'occupied' : 'vacant'}
                  >
                    {isOccupied ? 'Motion detected' : 'No motion'}
                  </span>
                </div>
              </div>
            )
          case 'info':
            return (
              <div key="info" className={`occupancy-sensor-modal-classic__info${hl}`} data-modal-section="info">
                <div className="occupancy-sensor-modal-classic__section-label" data-modal-slot="info-label">DETECTION INFO</div>
                <div className="occupancy-sensor-modal-classic__info-row">
                  <span className="material-symbols-rounded occupancy-sensor-modal-classic__info-icon" data-modal-slot="info-row-icon">
                    schedule
                  </span>
                  <span className="occupancy-sensor-modal-classic__info-text" data-modal-slot="info-row-text">
                    {lastDetectedText}
                  </span>
                </div>
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
